import React, { type PropsWithChildren, useMemo } from 'react';

import { useIntl } from 'react-intl';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import AppsIcon from '@atlaskit/icon/core/apps';
import { ViewMoreModalParamsProvider } from '@atlassian/conversation-assistant-chat-prompt-input/ViewMoreModalParamsProvider';
import { useEnabledKnowledgeSources } from '@atlassian/conversation-assistant-store/controllers/connectors-store';
import type { SideNavModalItem } from '@atlassian/conversation-assistant-ui-components/ui/side-nav-modal/modal';
import { SideNavModalProvider } from '@atlassian/conversation-assistant-ui-components/ui/side-nav-modal/side-nav-modal-provider';

import { useRovoSettingsConfig } from '../../../common/utils/rovo-settings-config';

import { DiscoveryContent } from './discovery-content';
import { messages as discoveryMessages } from './discovery-content/messages';
import { useManageMemorySideNavModalItem } from './memory-content-container/MemoryContentContainer';
import { useSharedChatsSideNavModalItem } from './shared-chats-content/useSharedChatsSideNavModalItem';

const useDiscoverySideNavModalItem = (): SideNavModalItem => {
	const { formatMessage } = useIntl();
	const [knowledgeSources] = useEnabledKnowledgeSources();

	return useMemo(
		() => ({
			id: 'discovery',
			label: formatMessage(discoveryMessages.sideNavLabel),
			icon: <AppsIcon label="" />,
			contentHeader: formatMessage(discoveryMessages.contentHeader),
			content: () => <DiscoveryContent knowledgeSources={knowledgeSources} />,
		}),
		[formatMessage, knowledgeSources],
	);
};

export const RovoSettingsModalSideNavModalProvider = ({
	children,
	disabled,
}: PropsWithChildren<{ disabled?: boolean }>): React.JSX.Element => {
	const manageMemoryItem = useManageMemorySideNavModalItem();
	const discoveryItem = useDiscoverySideNavModalItem();
	const sharedChatsItem = useSharedChatsSideNavModalItem();
	const { surface } = useRovoSettingsConfig();

	const isMemoryManagementUxEnabled = FeatureGates.getExperimentValue(
		'memory_management_ux',
		'isEnabled',
		false,
		{
			fireExperimentExposure: false,
		},
	);

	const isConversationShareMgmtEnabled = FeatureGates.getExperimentValue(
		'rovo_conversation_sharing',
		'isEnabled',
		false,
	);

	const items = useMemo(
		() => [
			...(isMemoryManagementUxEnabled ? [manageMemoryItem] : []),
			...(isConversationShareMgmtEnabled ? [sharedChatsItem] : []),
			discoveryItem,
		],
		[
			manageMemoryItem,
			isMemoryManagementUxEnabled,
			discoveryItem,
			sharedChatsItem,
			isConversationShareMgmtEnabled,
		],
	);

	return (
		<ViewMoreModalParamsProvider surface={surface}>
			<SideNavModalProvider items={items} disabled={disabled} testId="rovo-settings-modal">
				{children}
			</SideNavModalProvider>
		</ViewMoreModalParamsProvider>
	);
};
